import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash-es'
import { connect, useSelector } from 'react-redux'
import { useTrackingTrigger } from '@indeed/flex-tracking-context'

import { PreferencesListSection } from 'syft-acp-core/components/ClientPreferences'
import { MatchingExplainability } from 'syft-acp-core/components/MatchingExplainability'
import { CompliancesListSection } from 'syft-acp-core/components/Compliances'
import RecordSection from 'syft-acp-core/components/RecordSection'
import { Button } from 'syft-acp-atoms/Button'
import Modal from 'syft-acp-core/components/Modal/Modal'
import moment from 'moment'
import entityDetail from 'syft-acp-util/entityDetail'
import { TypeLabel } from 'syft-acp-core/components/TypeMaps'
import ListingJobDetailControls from './ListingJobDetailControls'
import ListingWarningModal from './ListingWarningModal'
import { jobStructure } from './structure'
import { TYPES_MAP } from './IndividualBookingTypes'
import { useFetchDisabledSkills } from 'syft-acp-core/api/resources/skills'
import { extendBooking, extendAgencyBooking } from 'syft-acp-core/store/jobs/actions'
import { useFlexFlagIsOn } from '@indeed/flex-feature-flags'
import { extendLTA, fetchLTAExtensions, cancelLTAExtension } from 'syft-acp-core/store/jobs/actions'
import { fetchAdminShiftListing } from 'syft-acp-core/store/listing-shifts/actions'
import { fetchAdminBookings } from 'syft-acp-core/store/listings/actions'
import { currentIntl } from 'syft-acp-util/intl'
import { BulkShiftRateUpdateModal } from 'syft-acp-core/components/Modal/BulkShiftRateUpdateModal'

import { trackingEvents } from '../../tracking'

import './ListingJobDetail.css'

const EMPTY_IN_FLIGHT = {}

const ListingJobDetail = ({
  id,
  roleID,
  listingID,
  data,
  isPristine,
  actionUpdate,
  employerID,
  employerName,
  venueID,
  shift,
  dispatch,
  allSkills,
  selectedAreaId,
}) => {
  const [updatedData, setUpdatedData] = useState({})
  const [showLTAModal, setShowLTAModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showBulkRateModal, setShowBulkRateModal] = useState(false)
  const [selectedDates, setSelectedDates] = useState([])
  const [currentMonth, setCurrentMonth] = useState(moment())
  const [extendingLTA, setExtendingLTA] = useState(false)
  const extendBookingInFlight = useSelector(state => state.jobs.extendBookingInFlight || EMPTY_IN_FLIGHT)

  const triggerEvent = useTrackingTrigger()

  const isCompliancePoliciesFeatureEnabled = useFlexFlagIsOn('pte_16678_admin_compliance_policies')

  const filteredJobTimeDependentRates = useMemo(() => {
    const jobTimeDependentRates = get(data, 'job_time_dependent_rates', [])
    return jobTimeDependentRates.map(timeDependentRate => {
      const shiftTDRs = get(shift, 'job_time_dependent_rates', []).map(item => item.id)
      if (shiftTDRs.includes(timeDependentRate.id)) {
        return { ...timeDependentRate, selected: true }
      }
      return timeDependentRate
    })
  }, [data, shift])
  const handlePreferencesSave = () => {
    triggerEvent(trackingEvents.LISTING_INFO.PREFERENCES.SAVED, { jobId: id })
  }

  const handleVenuePreferencesLink = () => {
    triggerEvent(trackingEvents.LISTING_INFO.VENUE_PREFERENCES.CLICKED, { jobId: id })
  }

  const handleExtendLTAClick = () => {
    setShowLTAModal(true)
  }

  const handleCloseLTAModal = () => {
    setShowLTAModal(false)
    setSelectedDates([])
    setCurrentMonth(moment())
  }

  const handleDateClick = dateStr => {
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr))
    } else {
      setSelectedDates([...selectedDates, dateStr])
    }
  }

  // Find the latest shift start time from the job data
  const getLatestShiftDate = () => {
    if (!data?.shifts || data.shifts.length === 0) {
      return null
    }

    const latestShift = data.shifts.reduce((latest, currentShift) => {
      const shiftStartTime = moment(currentShift.start_time)
      const latestStartTime = moment(latest.start_time)
      return shiftStartTime.isAfter(latestStartTime) ? currentShift : latest
    })

    return moment(latestShift.start_time)
  }

  const latestShiftDate = getLatestShiftDate()

  // Determine if we should start the week on Sunday (US) or Monday (UK/GB)
  const isUSLocale = currentIntl.countryCode === 'US'
  const weekStartsOnSunday = isUSLocale

  const generateCalendar = () => {
    const startOfMonth = currentMonth.clone().startOf('month')
    const endOfMonth = currentMonth.clone().endOf('month')

    // Create a set of shift dates for fast lookup
    const shiftDates = new Set()
    if (data?.shifts) {
      data.shifts.forEach(jobShift => {
        const shiftDate = moment(jobShift.start_time).format('YYYY-MM-DD')
        shiftDates.add(shiftDate)
      })
    }

    // Calculate week boundaries based on desired week start
    const firstDayOfWeek = weekStartsOnSunday ? 0 : 1 // 0 = Sunday, 1 = Monday

    const startDate = startOfMonth.clone()
    const daysFromWeekStart = (startDate.day() - firstDayOfWeek + 7) % 7
    startDate.subtract(daysFromWeekStart, 'days')

    const endDate = endOfMonth.clone()
    const daysToWeekEnd = (firstDayOfWeek + 6 - endDate.day()) % 7
    endDate.add(daysToWeekEnd, 'days')

    const calendar = []
    const current = startDate.clone()

    while (current.isSameOrBefore(endDate, 'day')) {
      const week = []
      for (let i = 0; i < 7; i++) {
        // Determine if this date should be disabled
        let isPast = current.isBefore(moment(), 'day') // Before today

        // If we have a latest shift date, disable dates on or before that date
        if (latestShiftDate) {
          isPast = isPast || current.isSameOrBefore(latestShiftDate, 'day')
        }

        const dateStr = current.format('YYYY-MM-DD')
        week.push({
          date: current.clone(),
          dateStr,
          isCurrentMonth: current.isSame(currentMonth, 'month'),
          isToday: current.isSame(moment(), 'day'),
          isPast,
          hasShift: shiftDates.has(dateStr),
        })
        current.add(1, 'day')
      }
      calendar.push(week)
    }
    return calendar
  }

  const nextMonth = () => setCurrentMonth(currentMonth.clone().add(1, 'month'))
  const prevMonth = () => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))

  const handleExtendLTA = () => {
    setExtendingLTA(true)
    dispatch(extendLTA(Number(id), selectedDates))
    handleCloseLTAModal()
  }

  const handleCancelLTA = () => {
    dispatch(cancelLTAExtension(Number(id)))
    setShowCancelModal(false)
  }

  const onUpdate = (path, value, ...rest) => {
    const newState = updatedData
    newState[path] = value
    setUpdatedData(newState)
    actionUpdate(path, value, ...rest)
  }
  const { disabledSkillIds } = useFetchDisabledSkills(dispatch)
  const skills = data.skills || (allSkills && data.skill_ids?.map(skillId => ({ ...allSkills[skillId] })))
  const essentialSkills =
    data.essential_skills ||
    data?.essential_skill_ids?.map(eSkillId => skills?.find(skill => eSkillId === skill.id))

  const showExtendJobSection = !data.bookable_individually

  // Fetch LTA extensions data when component mounts or ID changes
  // Only fetch LTA extensions if the job is not bookable individually as the section
  // is only shown if the job is not bookable individually
  useEffect(() => {
    if (id && showExtendJobSection) {
      dispatch(fetchLTAExtensions(Number(id)))
    }
  }, [dispatch, id, showExtendJobSection])

  useEffect(() => {
    if (data.ltaExtensions) {
      setExtendingLTA(false)
    }
  }, [data.ltaExtensions])

  return (
    <div className="listing-job-detail">
      <ListingJobDetailControls
        updatedData={updatedData}
        data={data}
        isPristine={isPristine}
        setUpdatedData={setUpdatedData}
        listingID={listingID}
        shift={shift}
      />
      <RecordSection
        title="Job information"
        data={{
          ...data,
          employerID,
          venueID,
          disabledSkills: disabledSkillIds,
          essential_skills: essentialSkills,
        }}
        structure={jobStructure(filteredJobTimeDependentRates, selectedAreaId).filter(
          item => !(item?.[0] === 'open_to_discuss_competencies'),
        )}
        update={onUpdate}
        titleLabels={
          <span>
            <TypeLabel value={data.bookable_individually} typesMap={TYPES_MAP} />
            {data.rate_card_id && (
              <>
                {' | '}
                <button
                  type="button"
                  onClick={() => setShowBulkRateModal(true)}
                  style={{
                    fontSize: '0.875rem',
                    marginLeft: '0.5rem',
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Bulk update shift rates →
                </button>
              </>
            )}
          </span>
        }
      />
      <BulkShiftRateUpdateModal
        isOpen={showBulkRateModal}
        onClose={() => setShowBulkRateModal(false)}
        jobId={Number(id)}
        onSuccess={() => {
          // Refresh the currently displayed shift to show updated rates
          // Note: We don't need fetchAdminJob because we're only modifying existing
          // shift rates, not adding/removing shifts (unlike LTA extension)
          if (shift?.id && listingID) {
            dispatch(fetchAdminShiftListing(listingID, shift.id))
            dispatch(fetchAdminBookings(shift.id, Number(id)))
          }
        }}
      />
      <ListingWarningModal />
      <div className="col-md-12 " style={{ marginTop: 20 }}>
        <MatchingExplainability
          venueID={venueID}
          jobId={id}
          employerName={employerName}
          employerID={employerID}
        />
      </div>
      <div className="col-md-12 " style={{ marginTop: 20 }}>
        <PreferencesListSection
          key={id}
          employerID={employerID}
          workLocationID={venueID}
          jobID={id}
          title="Fulfilment preferences"
          callbackOnSave={handlePreferencesSave}
          callbackOnVenuePreferences={handleVenuePreferencesLink}
        />
      </div>

      {isCompliancePoliciesFeatureEnabled ? (
        <div className="col-md-12 " style={{ marginTop: 20 }}>
          <CompliancesListSection key={id} employerID={employerID} roleID={roleID} title="Compliances" />
        </div>
      ) : null}

      {showExtendJobSection ? (
        <>
          <div className="col-md-12 " style={{ marginTop: 20 }}>
            <RecordSection title="Extend job">
              <div style={{ padding: '20px' }}>
                {/* LTA-extension warning (PTE-51961) */}
                <div style={{ marginBottom: 15 }}>
                  <strong>
                    This tool prevents offers being sent for extended LTAs so the shifts are protected for the
                    existing booked workers. Please do not use it to extend non-LTA jobs
                  </strong>
                </div>

                {/* Basic Info */}
                <div style={{ marginBottom: 15 }}>
                  Extending the job will duplicate the last shift, so be sure any bookings are reflected on
                  the last shift.
                  <br />
                  NOTE that that Granular Shift Edits will not currently be extended.
                </div>

                <div style={{ marginBottom: 0 }}>There are two steps to extending an assignment:</div>

                <div style={{ marginBottom: 0 }}>1. Creating the shifts</div>

                <div style={{ marginBottom: 15 }}>2. Booking the workers</div>

                <div style={{ marginBottom: 15 }}></div>

                {data.ltaExtensions?.status === 'in_progress' && (
                  <>
                    <h4 style={{ marginBottom: 15 }}>Book Workers</h4>

                    <div style={{ marginBottom: 15 }}>
                      Click 'Extend Booking' to book existing workers. Unfilled shifts will need manual
                      assignment.
                    </div>
                  </>
                )}

                <div style={{ marginBottom: 15 }}>
                  <strong>Bookable Individually: </strong>
                  {data.bookable_individually ? 'Yes' : 'No'}
                </div>

                <div style={{ marginBottom: 15 }}>
                  <strong>Extension in progress: </strong>
                  {data.ltaExtensions?.status === 'in_progress' ? 'Yes' : 'No'}
                </div>

                {/* Extension Details */}
                {data.ltaExtensions && (
                  <div>
                    <h4 style={{ marginBottom: 15 }}>
                      {data.ltaExtensions.status === 'in_progress'
                        ? 'Current Extension in Progress'
                        : 'Last completed extension'}
                    </h4>

                    {data.ltaExtensions.status !== 'in_progress' && (
                      <div style={{ marginBottom: 15 }}>
                        <strong>Last extension completion status: </strong>
                        {data.ltaExtensions.status}
                      </div>
                    )}

                    <div style={{ marginBottom: 10 }}>
                      <strong>Job's last shift before the extension: </strong>
                      {data.ltaExtensions.former_last_shift_date}
                    </div>

                    <div style={{ marginBottom: 20 }}>
                      <strong>Job's last shift after the extension: </strong>
                      {data.ltaExtensions.new_last_shift_date}
                    </div>

                    {/* Workers Table */}
                    {data.ltaExtensions.lta_extensions_workers &&
                      data.ltaExtensions.lta_extensions_workers.length > 0 && (
                        <div>
                          <br />
                          <h4 style={{ marginBottom: 15 }}>
                            {data.ltaExtensions.status === 'in_progress'
                              ? 'Bookings to extend'
                              : 'Bookings extended'}
                          </h4>

                          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                <th
                                  style={{ padding: '10px', textAlign: 'left', border: '1px solid #dee2e6' }}
                                >
                                  Worker Type
                                </th>
                                <th
                                  style={{ padding: '10px', textAlign: 'left', border: '1px solid #dee2e6' }}
                                >
                                  Worker
                                </th>
                                <th
                                  style={{ padding: '10px', textAlign: 'left', border: '1px solid #dee2e6' }}
                                >
                                  Status
                                </th>
                                <th
                                  style={{ padding: '10px', textAlign: 'left', border: '1px solid #dee2e6' }}
                                >
                                  Errors
                                </th>
                                {data.ltaExtensions.status === 'in_progress' && (
                                  <th
                                    style={{
                                      padding: '10px',
                                      textAlign: 'center',
                                      border: '1px solid #dee2e6',
                                    }}
                                  >
                                    Actions
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {data.ltaExtensions.lta_extensions_workers.map((worker, index) => {
                                const workerType = worker.agency_worker_id ? 'Agency' : 'Worker'
                                const workerId = worker.agency_worker_id || worker.worker_id
                                const workerName = worker.agency_worker_name || worker.worker_name

                                return (
                                  <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>
                                      {workerType}
                                    </td>
                                    <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>
                                      {workerId} - {workerName}
                                    </td>
                                    <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>
                                      {worker.status}
                                    </td>
                                    <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>
                                      {worker.booking_errors || '-'}
                                    </td>
                                    {data.ltaExtensions.status === 'in_progress' && (
                                      <td
                                        style={{
                                          padding: '10px',
                                          textAlign: 'center',
                                          border: '1px solid #dee2e6',
                                        }}
                                      >
                                        {worker.status === 'pending' && (
                                          <>
                                            {worker.worker_id && (
                                              <Button
                                                kind="primary"
                                                size="small"
                                                disabled={Boolean(
                                                  extendBookingInFlight[`${id}_worker_${worker.worker_id}`],
                                                )}
                                                onClick={() => dispatch(extendBooking(id, worker.worker_id))}
                                              >
                                                {extendBookingInFlight[`${id}_worker_${worker.worker_id}`]
                                                  ? 'Extending...'
                                                  : 'Extend Booking'}
                                              </Button>
                                            )}
                                            {worker.agency_worker_id && (
                                              <Button
                                                kind="primary"
                                                size="small"
                                                disabled={Boolean(
                                                  extendBookingInFlight[
                                                    `${id}_agency_${worker.agency_worker_id}`
                                                  ],
                                                )}
                                                onClick={() =>
                                                  dispatch(extendAgencyBooking(id, worker.agency_worker_id))
                                                }
                                              >
                                                {extendBookingInFlight[
                                                  `${id}_agency_${worker.agency_worker_id}`
                                                ]
                                                  ? 'Extending...'
                                                  : 'Extend Agency Booking'}
                                              </Button>
                                            )}
                                          </>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                  </div>
                )}

                {/* Action Buttons at Bottom */}
                <div style={{ marginTop: 20 }}>
                  {data.ltaExtensions?.status === 'in_progress' ? (
                    <Button kind="danger" onClick={() => setShowCancelModal(true)}>
                      Done adding workers
                    </Button>
                  ) : (
                    <Button kind="primary" disabled={extendingLTA} onClick={handleExtendLTAClick}>
                      {extendingLTA ? 'Extending...' : 'Start extending'}
                    </Button>
                  )}
                </div>
              </div>
            </RecordSection>
          </div>

          <Modal
            header="Extend job"
            isShown={showLTAModal}
            onClose={handleCloseLTAModal}
            onConfirm={handleExtendLTA}
            confirmationText="Create shifts"
            cancelText="Cancel"
            canSubmit={selectedDates.length > 0}
          >
            <div style={{ padding: '20px' }}>
              <p>
                <strong>Select additional dates</strong>
              </p>
              {latestShiftDate && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#495057',
                  }}
                >
                  <strong>Note:</strong> You can only select dates after{' '}
                  {latestShiftDate.format('YYYY-MM-DD')} (the last scheduled shift)
                </div>
              )}

              <div
                style={{ marginTop: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}
              >
                {/* Calendar Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                  }}
                >
                  <Button kind="secondary" size="small" onClick={prevMonth}>
                    ‹
                  </Button>
                  <h4 style={{ margin: 0 }}>{currentMonth.format('MMMM YYYY')}</h4>
                  <Button kind="secondary" size="small" onClick={nextMonth}>
                    ›
                  </Button>
                </div>

                {/* Day Headers */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px',
                    marginBottom: '5px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                  }}
                >
                  {(weekStartsOnSunday
                    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                  ).map(day => (
                    <div key={day} style={{ padding: '8px 4px' }}>
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                {generateCalendar().map((week, weekIndex) => (
                  <div
                    key={weekIndex}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '2px',
                      marginBottom: '2px',
                    }}
                  >
                    {week.map((dayObj, dayIndex) => {
                      const isSelected = selectedDates.includes(dayObj.dateStr)
                      const isDisabled = dayObj.isPast

                      return (
                        <div
                          key={dayIndex}
                          onClick={() => !isDisabled && handleDateClick(dayObj.dateStr)}
                          style={{
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isSelected
                              ? '#007bff'
                              : dayObj.hasShift && dayObj.isPast
                              ? '#ff50d9'
                              : 'transparent',
                            color: isSelected ? 'white' : dayObj.isCurrentMonth ? 'black' : '#ccc',
                            border: dayObj.isToday ? '2px solid #28a745' : '1px solid transparent',
                            borderRadius: '4px',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            fontWeight: dayObj.isToday ? 'bold' : 'normal',
                            opacity: isDisabled ? 0.3 : 1,
                            fontSize: '14px',
                            transition: 'background-color 0.2s',
                          }}
                        >
                          {dayObj.date.format('D')}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>

              {selectedDates.length > 0 && (
                <div style={{ marginTop: '15px', textAlign: 'center', color: '#666' }}>
                  <strong>
                    {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
                  </strong>
                </div>
              )}
            </div>
          </Modal>

          <Modal
            header="Cancel Extension"
            isShown={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            onConfirm={handleCancelLTA}
            confirmationText="Yes, I'm done adding workers"
            cancelText="Continue Extending"
            confirmationButtonKind="danger"
          >
            <div style={{ padding: '20px' }}>
              <p>
                If you mark this extension as "done" now and extend the job again, any bookings that haven't
                been extended will be quite laborious to then manually assign.
              </p>
              <p>
                <strong>Are you sure you want to mark this extension as "done"?</strong>
              </p>
            </div>
          </Modal>
        </>
      ) : null}
    </div>
  )
}

ListingJobDetail.propTypes = {
  id: PropTypes.string.isRequired,
  listingID: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  employerID: PropTypes.number.isRequired,
  employerName: PropTypes.string.isRequired,
  venueID: PropTypes.number.isRequired,
  roleID: PropTypes.number,
  isPristine: PropTypes.bool.isRequired,
  listingShift: PropTypes.object.isRequired,
  actionUpdate: PropTypes.func.isRequired,
  shift: PropTypes.object,
  dispatch: PropTypes.func,
  allSkills: PropTypes.object.isRequired,
}

const stateConnector = connect(state => ({
  allSkills: get(state, 'skills.entityMap', {}),
  selectedAreaId: get(state, 'dataTabs.area_id'),
}))

export default entityDetail('jobs', stateConnector(ListingJobDetail))
